import Decimal from 'decimal.js';

interface OwningIndividualShape {
  id: string;
  dispositionScenarios?:
    | {
        id: string;
        reviewedAt?: Date | null;
      }[]
    | null;
}

interface OwnedByStakeShape {
  ownershipPercentage?: Decimal | string | number | null;
  owningIndividual?: OwningIndividualShape | null;
  owningEntity?: { id: string } | null;
}

export type BusinessDetailBadgeState =
  | { showBadge: false }
  | { showBadge: true; targetClientProfileId: string };

// "No DPs" matches the same definition shouldShowNoDispositionsBadge uses:
// no scenarios at all, or no scenario with a reviewedAt timestamp.
function ownerHasNoReviewedDispositions(
  scenarios: OwningIndividualShape['dispositionScenarios']
): boolean {
  return !scenarios?.some((scenario) => scenario.reviewedAt);
}

/**
 * Decide whether the "No dispositions" badge should render for a business
 * entity, and where its "Add dispositions" click should target. Used by both
 * the entity detail page and the trusts-and-businesses accordion so they stay
 * in sync.
 *
 * Returns `{ showBadge: true, targetClientProfileId }` when ALL of:
 *   1. Every ownership stake is held directly by an individual (no
 *      entity-owned stakes — trust-owned cases are explicitly out of scope).
 *   2. Every owning individual is a primary client of the household.
 *   3. At least one primary-client owner has no reviewed dispositions.
 *
 * The click target is the primary-client owner with the largest ownership
 * percentage who has no reviewed dispositions, tie-broken by first-by-stake
 * order in `ownedByStakes`.
 */
export function getBusinessDetailBadgeState(
  ownedByStakes: readonly OwnedByStakeShape[] | null | undefined,
  primaryClientIds: readonly string[]
): BusinessDetailBadgeState {
  if (!ownedByStakes || ownedByStakes.length === 0) {
    return { showBadge: false };
  }
  const primaryClientIdSet = new Set(primaryClientIds);

  let bestTarget: { clientProfileId: string; percentage: Decimal } | null =
    null;

  for (const stake of ownedByStakes) {
    // Rule 1: any entity-owned stake disqualifies.
    if (stake.owningEntity) return { showBadge: false };

    const owningIndividual = stake.owningIndividual;
    if (!owningIndividual) return { showBadge: false };

    // Rule 2: every owning individual must be a primary client.
    if (!primaryClientIdSet.has(owningIndividual.id)) {
      return { showBadge: false };
    }

    if (
      !ownerHasNoReviewedDispositions(owningIndividual.dispositionScenarios)
    ) {
      continue;
    }

    const percentage = new Decimal(stake.ownershipPercentage ?? 0);
    // Strict `>` preserves the first-by-stake-order tie-break.
    if (!bestTarget || percentage.greaterThan(bestTarget.percentage)) {
      bestTarget = {
        clientProfileId: owningIndividual.id,
        percentage,
      };
    }
  }

  // Rule 3: need at least one primary-client owner without DPs.
  if (!bestTarget) return { showBadge: false };

  return { showBadge: true, targetClientProfileId: bestTarget.clientProfileId };
}
